import { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import InputLabel from '@mui/material/InputLabel'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Grid from '@mui/material/Grid'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import Box from '@mui/material/Box'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Clear from '@mui/icons-material/Clear'
import Delete from '@mui/icons-material/Delete'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { metadataDRMRegex } from '../../helpers/Regexes'
import { fetchUsageRights } from '../../store/metadata/actionCreator'

/**
 * Usage Rights card for BulkEditBar.
 *
 * - Large text field accepts multiple comma / space separated DRM IDs.
 * - Each entry is validated via fetchUsageRights; chips reflect loading / valid / error.
 * - Validated DRM details are shown in expandable accordions.
 * - When isBulkEdit (>1 asset selected), radio buttons let the user choose
 *   between "Add to existing" and "Replace existing".
 */
export const UsageInfoCard = ({
  updateParentState = () => {},
  selectedAssetGoldMetadata = {},
  isBulkEdit = false,
}) => {
  const [drmIdInput, setDrmIdInput] = useState('')
  const [drmIds, setDrmIds] = useState([])
  const [expandedPanel, setExpandedPanel] = useState(false)
  const [radioBtnUsage, setRadioBtnUsage] = useState('APPEND')

  const validatingRef = useRef(new Set())

  // ─── Pre-fill from gold metadata (single-asset edit) ───────────────
  useEffect(() => {
    const usageInfoArray =
      selectedAssetGoldMetadata?.metadata?.usage_info || []
    if (usageInfoArray.length === 0) return

    const prefilled = usageInfoArray.map((info) => ({
      label: info.drm_id || '',
      isLoading: false,
      isError: false,
      details: {
        drm_id: info.drm_id || 'N/A',
        holder_name: info.rights_holder_name || 'N/A',
        holder_type: info.rights_holder_type || 'N/A',
        expiration_time: info.expiration_date || 'N/A',
        available_channels: info.available_channels || 'N/A',
        territory: info.available_territories || 'N/A',
        buyer: info.buyer || 'N/A',
        terms: info.terms || 'N/A',
        usage_rights_language: info.usage_rights_language || 'N/A',
      },
    }))

    setDrmIds(prefilled)
  }, [selectedAssetGoldMetadata])

  // ─── Propagate validated DRM data to parent ─────────────────────────
  const propagateToParent = useCallback(
    (chips, radioValue) => {
      const validChips = chips.filter(
        (c) => !c.isError && !c.isLoading && c.details,
      )
      const usageInformation = validChips.map((c) => ({
        drmId: c.label,
        rightsHolder: c.details.holder_name || '',
        rightsHolderType:
          c.details.holder_type || c.details.rights_holder_type || '',
        expirationDate: c.details.expiration_time || '',
        availableChannels: c.details.available_channels || '',
      }))
      updateParentState({
        usageInformation,
        radioBtnUsage: radioValue,
        unsavedChanges: true,
      })
    },
    [updateParentState],
  )

  // ─── Validate a single DRM ID ──────────────────────────────────────
  const validateDrm = useCallback(
    (drmId) =>
      new Promise((resolve) => {
        fetchUsageRights(drmId, null, (_req, response, type) => {
          if (
            type === 'success' &&
            Array.isArray(response) &&
            response.length > 0
          ) {
            setDrmIds((prev) => {
              const updated = prev.map((chip) =>
                chip.label === drmId
                  ? { ...chip, isLoading: false, isError: false, details: response[0] }
                  : chip,
              )
              propagateToParent(updated, radioBtnUsage)
              return updated
            })
          } else {
            setDrmIds((prev) =>
              prev.map((chip) =>
                chip.label === drmId
                  ? { ...chip, isLoading: false, isError: true }
                  : chip,
              ),
            )
          }
          validatingRef.current.delete(drmId)
          resolve()
        })
      }),
    [propagateToParent, radioBtnUsage],
  )

  // Ref to track current chips synchronously (avoids async setState issues)
  const drmIdsRef = useRef([])
  useEffect(() => {
    drmIdsRef.current = drmIds
  }, [drmIds])

  // ─── Process an array of raw entries ────────────────────────────────
  const processDrmIds = useCallback(
    async (entries) => {
      const trimmed = Array.from(
        new Set(entries.map((e) => e.trim()).filter(Boolean)),
      )

      // Use ref for synchronous access to current state
      const currentChips = drmIdsRef.current
      const existing = new Set(currentChips.map((c) => c.label))

      // Filter out already existing and already validating IDs
      const newIds = trimmed.filter(
        (id) => !existing.has(id) && !validatingRef.current.has(id)
      )

      if (newIds.length === 0) {
        setDrmIdInput('')
        return
      }

      // Build new chips and track which need validation
      const idsToValidate = []
      const newChips = newIds.map((id) => {
        const valid = metadataDRMRegex.test(id)
        if (valid) {
          validatingRef.current.add(id)
          idsToValidate.push(id)
        }
        return { label: id, isLoading: valid, isError: !valid, details: null }
      })

      // Update state
      setDrmIds((prev) => [...prev, ...newChips])
      setDrmIdInput('')

      // Validate all new valid IDs
      if (idsToValidate.length > 0) {
        await Promise.all(idsToValidate.map(validateDrm))
      }
    },
    [validateDrm],
  )

  // ─── Event handlers ─────────────────────────────────────────────────
  const handlePaste = useCallback(
    (event) => {
      event.preventDefault()
      const text = event.clipboardData.getData('text') || ''
      processDrmIds(text.split(/[,\s]+/))
    },
    [processDrmIds],
  )

  const handleDeleteChip = useCallback(
    (chipLabel) => {
      validatingRef.current.delete(chipLabel)
      setDrmIds((prev) => {
        const updated = prev.filter((c) => c.label !== chipLabel)
        propagateToParent(updated, radioBtnUsage)
        return updated
      })
    },
    [propagateToParent, radioBtnUsage],
  )

  const handleKeyDown = useCallback(
    (event) => {
      // Read directly from event.target.value to avoid stale closure issues
      const value = (event.target.value || '').trim()

      if (event.key === 'Backspace' && value === '' && drmIds.length > 0) {
        event.preventDefault()
        const last = drmIds[drmIds.length - 1]?.label
        if (last) handleDeleteChip(last)
        return
      }

      if ((event.key === 'Enter' || event.key === ',') && value !== '') {
        event.preventDefault()
        processDrmIds(event.target.value.split(/[,\s]+/))
      }
    },
    [drmIds, processDrmIds, handleDeleteChip],
  )

  const handleRadioChange = useCallback(
    (event) => {
      const value = event.target.value
      setRadioBtnUsage(value)
      propagateToParent(drmIds, value)
    },
    [drmIds, propagateToParent],
  )

  const handleAccordionToggle = useCallback(
    (panel) => (_event, expanded) => {
      setExpandedPanel(expanded ? panel : false)
    },
    [],
  )

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <Accordion defaultExpanded style={{ margin: 0 }}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="usage-rights-content"
        id="usage-rights-header"
      >
        <Typography sx={{ fontSize: '16px', fontWeight: 400 }}>
          Usage Rights
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {/* Radio buttons — only for bulk (multi-asset) edit */}
        {isBulkEdit && (
          <RadioGroup
            row
            value={radioBtnUsage}
            onChange={handleRadioChange}
            sx={{ mb: 1, justifyContent: 'flex-start' }}
          >
            <FormControlLabel
              value="APPEND"
              control={<Radio size="small" />}
              label="Adding to existing data"
            />
            <FormControlLabel
              value="MERGE"
              control={<Radio size="small" />}
              label="Replacing existing data"
            />
          </RadioGroup>
        )}

        {/* DRM ID chip input */}
        <TextField
          data-cy="urDrmId"
          fullWidth
          onChange={(e) => setDrmIdInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          value={drmIdInput}
          label="DRM ID(s)"
          placeholder="Add DRM ID"
          helperText="Use format DRMxxxxx. Paste a list or press Enter after each DRM ID."
          variant="outlined"
          sx={{
            '& .MuiFormHelperText-root': {
              color: '#d32f2f',
            },
            '& .MuiInputBase-root': {
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              rowGap: 0.5,
              maxHeight: 150,
              overflowY: 'auto',
              p: 1,
            },
            '& .MuiInputAdornment-root': {
              m: 0,
              maxWidth: '100%',
              flexBasis: '100%',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
            },
            '& .MuiInputBase-input': {
              flex: '1 0 120px',
              minWidth: 120,
              p: '6px 0',
            },
          }}
          InputProps={{
            startAdornment: (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {drmIds.map((chip, index) => (
                  <Tooltip
                    key={index}
                    title={
                      chip.isLoading
                        ? 'Validating...'
                        : chip.isError
                          ? 'Kindly check the DRM ID — it is either incorrect or unavailable.'
                          : 'Valid DRM ID'
                    }
                  >
                    <Chip
                      label={
                        chip.isLoading ? (
                          <CircularProgress size={16} sx={{ color: 'inherit' }} />
                        ) : (
                          chip.label
                        )
                      }
                      onDelete={() => handleDeleteChip(chip.label)}
                      sx={{
                        backgroundColor: chip.isLoading
                          ? '#FFECB3'
                          : chip.isError
                            ? '#FFCDD2'
                            : '#C8E6C9',
                        color: chip.isLoading
                          ? '#FF6F00'
                          : chip.isError
                            ? '#B71C1C'
                            : '#2E7D32',
                      }}
                      deleteIcon={
                        <Clear
                          style={{
                            color: chip.isLoading
                              ? '#FF6F00'
                              : chip.isError
                                ? '#B71C1C'
                                : '#2E7D32',
                            width: 16,
                            height: 16,
                          }}
                        />
                      }
                    />
                  </Tooltip>
                ))}
              </Box>
            ),
          }}
        />

        {/* Validated DRM detail accordions */}
        {drmIds
          .filter((chip) => chip.details)
          .map((chip, index) => (
            <Accordion
              key={`ur-acc-${index}`}
              style={{ boxShadow: 'none', marginTop: '0.5rem' }}
              expanded={expandedPanel === `usage-right-${index}`}
              onChange={handleAccordionToggle(`usage-right-${index}`)}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  border: '1px solid #0000003B',
                  minHeight: 'auto',
                  '& .MuiAccordionSummary-content': {
                    margin: 0,
                    overflow: 'hidden',
                    flex: 1,
                    minWidth: 0,
                  },
                  '& .MuiAccordionSummary-expandIconWrapper': {
                    flexShrink: 0,
                  },
                }}
              >
                <Grid
                  container
                  spacing={1}
                  sx={{ display: 'flex', alignItems: 'center', p: '0.5rem', flexWrap: 'nowrap', overflow: 'hidden' }}
                >
                  <Grid item xs={1.5} sx={{ minWidth: 0 }}>
                    <InputLabel shrink>DRM ID</InputLabel>
                    <span>{chip.label || 'N/A'}</span>
                  </Grid>
                  <Grid item xs={2} sx={{ minWidth: 0 }}>
                    <InputLabel shrink>Rights Holder</InputLabel>
                    <span>{chip.details.holder_name || 'N/A'}</span>
                  </Grid>
                  <Grid item xs={2} sx={{ minWidth: 0 }}>
                    <InputLabel shrink>Rights Holder Type</InputLabel>
                    <span>
                      {chip.details.holder_type ||
                        chip.details.rights_holder_type ||
                        'N/A'}
                    </span>
                  </Grid>
                  <Grid item xs={2} sx={{ minWidth: 0 }}>
                    <InputLabel shrink>Expiration Date</InputLabel>
                    <span>
                      {chip.details.expiration_time
                        ? new Date(chip.details.expiration_time).toLocaleDateString()
                        : 'N/A'}
                    </span>
                  </Grid>
                  <Grid item xs={3.5} sx={{ minWidth: 0 }}>
                    <InputLabel shrink>Available Channels</InputLabel>
                    <Box
                      sx={{
                        overflowX: 'auto',
                        whiteSpace: 'nowrap',
                        fontSize: '13px',
                        lineHeight: 1.4,
                        '&::-webkit-scrollbar': { height: 4 },
                        '&::-webkit-scrollbar-thumb': { background: '#bbb', borderRadius: 2 },
                      }}
                      title={chip.details.available_channels || 'N/A'}
                    >
                      {chip.details.available_channels || 'N/A'}
                    </Box>
                  </Grid>
                  <Grid item xs={1} sx={{ minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
                    <Delete
                      sx={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteChip(chip.label)
                      }}
                    />
                  </Grid>
                </Grid>
              </AccordionSummary>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                      Buyer
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                      {chip.details.buyer || 'N/A'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                      Territory
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                      {chip.details.territory || 'N/A'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                      Usage Rights Language
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                      {chip.details.usage_rights_language || 'N/A'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 500, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                      Terms
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                      {chip.details.terms || 'N/A'}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Accordion>
          ))}
      </AccordionDetails>
    </Accordion>
  )
}

UsageInfoCard.propTypes = {
  updateParentState: PropTypes.func,
  selectedAssetGoldMetadata: PropTypes.object,
  isBulkEdit: PropTypes.bool,
}

export default UsageInfoCard